import api, { withLoading } from '@/api/api';

/**
 * Representa um participante vinculado a um business plan
 * 
 * @typedef {Object} ParticipanteVinculado
 * @property {number} id - Identificador único do participante
 * @property {string} nome - Nome completo do participante
 * @property {string} matricula - Número de matrícula
 * @property {string} perfil_nome - Nome do perfil profissional
 * @property {number} perfil_id - ID do perfil profissional
 * @property {string} budget_key - Chave de orçamento
 * @property {number} tipo_participante - Código do tipo de participante
 * @property {string} tipo_participante_label - Descrição do tipo de participante
 * @property {Object|null} perfil - Dados completos do perfil
 * @property {Object[]} perfis - Lista de perfis com vigências
 */

const resource = (empresaId, businessPlanId) => `v1/ativacaoped/${empresaId}/business-plans/${businessPlanId}/participantes`;
const LOADING_KEY = 'ativacaoped-business-plan-participantes';

/**
 * Lista participantes vinculados a um business plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @returns {Promise<{ data: ParticipanteVinculado[] }>} Lista de participantes vinculados
 */
export const listarParticipantesVinculados = (empresaId, businessPlanId) => {
  const baseUrl = resource(empresaId, businessPlanId);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl).then(({ data }) => data)
  );
};

/**
 * Vincula participantes a um business plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {number[]} participantesIds - Lista de IDs dos participantes a vincular
 * @returns {Promise<{ data: ParticipanteVinculado[] }>} Participantes vinculados
 */
export const vincularParticipantes = (empresaId, businessPlanId, participantesIds) => {
  const baseUrl = resource(empresaId, businessPlanId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, { participantes: participantesIds }).then(({ data }) => data)
  );
};

/**
 * Desvincula um participante de um business plan.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessPlanId - ID do Business Plan
 * @param {number|string} participanteId - ID do participante a desvincular
 * @returns {Promise<void>}
 */
export const desvincularParticipante = (empresaId, businessPlanId, participanteId) => {
  const baseUrl = resource(empresaId, businessPlanId);

  return withLoading(LOADING_KEY, () =>
    api.delete(`${baseUrl}/${participanteId}`).then(({ data }) => data)
  );
};

export default {
  listarParticipantesVinculados,
  vincularParticipantes,
  desvincularParticipante
};
